<template>
  <div class="flex w-full items-end flex-wrap gap-4 py-2" >
    <div class="w-full xl:w-1/6">
      <search-label-body label="팀(업체)명" :columns="gridTemplateColumns">
        <vs-select class="w-full" autocomplete v-model="팀업체명">
          <vs-select-item v-for="(option, index) in cfOptions.팀명" :key="index" :text="option" :value="option" />
        </vs-select>
      </search-label-body>
    </div>
    <div class="w-full xl:w-1/6">
      <search-label-body label="공장명" :columns="gridTemplateColumns">
        <vs-select class="w-full" autocomplete v-model="공장명" :disabled="팀업체명 === '전체'">
          <vs-select-item v-for="(option, index) in cfOptions.공장명" :key="index" :text="option" :value="option" />
        </vs-select>
      </search-label-body>
    </div>
    <div class="w-full xl:w-1/6">
      <search-label-body label="대분류" :columns="gridTemplateColumns">
        <vs-select class="w-full" autocomplete v-model="대분류" :disabled="팀업체명 === '전체' || 공장명 === '전체'">
          <vs-select-item v-for="(option, index) in cfOptions.대분류" :key="index" :text="option" :value="option" />
        </vs-select>
      </search-label-body>
    </div>
    <div class="w-full xl:w-1/6">
      <search-label-body label="중분류" :columns="gridTemplateColumns">
        <vs-select class="w-full" autocomplete v-model="중분류" :disabled="팀업체명 === '전체' || 공장명 === '전체' || 대분류 === '전체'">
          <vs-select-item v-for="(option, index) in cfOptions.중분류" :key="index" :text="option" :value="option" />
        </vs-select>
      </search-label-body>
    </div>
    <div class="w-full xl:w-1/6">
      <search-label-body label="소분류" :columns="gridTemplateColumns">
        <vs-select class="w-full" autocomplete v-model="소분류" :disabled="팀업체명 === '전체' || 공장명 === '전체' || 대분류 === '전체' || 중분류 === '전체'">
          <vs-select-item v-for="(option, index) in cfOptions.소분류" :key="index" :text="option" :value="option" />
        </vs-select>
      </search-label-body>
    </div>
  </div>
</template>

<script>
import SearchLabelBody from "@/views/list/components/base/SearchLabelBody.vue";
import SearchFilterItem from "@/views/list/components/base/SearchFilterItem.vue";
import { buildLevelOptions, loadClassificationTable } from "@/util/ClassificationTable";

export default {
  name: "SearchClassification",
  components: {
    SearchFilterItem,
    SearchLabelBody,
  },
  props: {
    label: {
      default: "공정분류",
      type: String,
    },
    value: Object,
    setRef: Function,
    readonly: Boolean,
    query: {
      type: Object,
      default: () => {
        return {
          'cf': '',
        }
      },
    },
    onClickSearch: Function,
    options: Object
  },
  watch: {
    value: {
      handler(value) {
        if (value && !this.isInitializing) {
          this.팀업체명 = value.팀업체명 || "전체";
          this.공장명 = value.공장명 || "전체";
          this.대분류 = value.대분류 || "전체";
          this.중분류 = value.중분류 || "전체";
          this.소분류 = value.소분류 || "전체";
        }
      },
      deep: true,
    },
    팀업체명() {
      if (this.isInitializing) return;
      this.공장명 = "전체";
      this.대분류 = "전체";
      this.중분류 = "전체";
      this.소분류 = "전체";
      // 팀업체명이 변경되면 공장명 옵션 로드
      if (this.팀업체명 !== "전체") {
        this.loadOptionsForLevel("공장명", [this.팀업체명]);
      }
      this.emitValue();
    },
    공장명() {
      if (this.isInitializing) return;
      this.대분류 = "전체";
      this.중분류 = "전체";
      this.소분류 = "전체";
      // 공장명이 변경되면 대분류 옵션 로드
      if (this.공장명 !== "전체") {
        this.loadOptionsForLevel("대분류", [this.팀업체명, this.공장명]);
      }
      this.emitValue();
    },
    대분류() {
      if (this.isInitializing) return;
      this.중분류 = "전체";
      this.소분류 = "전체";
      // 대분류가 변경되면 중분류 옵션 로드
      if (this.대분류 !== "전체") {
        this.loadOptionsForLevel("중분류", [this.팀업체명, this.공장명, this.대분류]);
      }
      this.emitValue();
    },
    중분류() {
      if (this.isInitializing) return;
      this.소분류 = "전체";
      // 중분류가 변경되면 소분류 옵션 로드
      if (this.중분류 !== "전체") {
        this.loadOptionsForLevel("소분류", [this.팀업체명, this.공장명, this.대분류, this.중분류]);
      }
      this.emitValue();
    },
    소분류() {
      if (this.isInitializing) return;
      this.emitValue();
    },
    fieldName() {
      if (this.isInitializing) return;
      this.팀업체명 = "전체";
      this.공장명 = "전체";
      this.대분류 = "전체";
      this.중분류 = "전체";
      this.소분류 = "전체";
      // 사업장이 변경되면 팀업체명 옵션 로드
      this.loadOptionsForLevel("팀업체명");
    }
  },
  computed: {
    layout() {
      return this.options && this.options.layout ? this.options.layout : 'default';
    },
    isMobile () {
      return !(this.$store.state.verticalNavMenuWidth === 'default')
    },
    initData() {
      // 기본값 설정
      const defaultClassification = {
        팀업체명: "전체",
        공장명: "전체",
        대분류: "전체",
        중분류: "전체",
        소분류: "전체"
      };

      // options에서 기본값이 있으면 사용
      if (this.options && this.options['default']) {
        Object.assign(defaultClassification, this.options['default']);
      }

      // 쿼리에서 값 가져오기
      const query = this.$route.query;
      const queryKeys = this.query; // { cf_team: "팀업체명", cf_factory: "공장명", ... }

      if (queryKeys) {
        // query 객체의 각 키를 순회하면서 URL 쿼리에서 값을 가져옴
        Object.keys(queryKeys).forEach(urlKey => {
          const dataKey = queryKeys[urlKey]; // "팀업체명", "공장명" 등
          if (query[urlKey]) {
            defaultClassification[dataKey] = query[urlKey];
          }
        });
      }

      // value prop이 있으면 우선 사용
      if (this.value) {
        Object.assign(defaultClassification, this.value);
      }

      return defaultClassification;
    },
    gridTemplateColumns() {
      return this.options && this.options.gridTemplateColumns ? this.options.gridTemplateColumns : "80px 1fr";
    },
    nav() {
      const nav = this.options && this.options.nav ? this.options.nav : null;
      return nav;
    },

    // List.js 의 components 중 search-select-field 에 사용한 value 값
    searchFieldKey() {
      return this.options && this.options.searchFieldKey ? this.options.searchFieldKey : "field";
    },

    // List.js 에서 사용한 SearchSelectField 에서 선택된 사업장의 Id
    fieldName() {
      const nav = this.nav
      const key = this.searchFieldKey

      if (!nav || !key || !nav[key]) return 0

      return nav[key].fieldName || 0
    },
    boardIds() {
      return this.options && this.options.boardIds ? this.options.boardIds : [];
    },

    // 공장명/대분류/중분류/소분류 옵션의 원본이 되는 공정분류표 게시판 id
    // 813 -> 736(공정분류표(직영)), 814 -> 895(공정분류표(수급업체))
    classificationBoardId() {
      return (this.options && this.options.classificationBoardId) || 736;
    },
  },
  data() {
    return {
      팀업체명: "전체",
      공장명: "전체",
      대분류: "전체",
      중분류: "전체",
      소분류: "전체",
      cfOptions: {
        팀명: ["전체"],
        공장명: ["전체"],
        대분류: ["전체"],
        중분류: ["전체"],
        소분류: ["전체"],
      },
      // 초기화 중인지 확인하는 플래그
      isInitializing: false,
    };
  },
  async created() {
    this.isInitializing = true;
    const initData = this.initData;

    this.팀업체명 = initData.팀업체명;
    this.공장명 = initData.공장명;
    this.대분류 = initData.대분류;
    this.중분류 = initData.중분류;
    this.소분류 = initData.소분류;
    this.emitValue();

    // 1. 팀업체명 옵션 로드
    await this.loadOptionsForLevel("팀업체명");

    // 2. 팀업체명이 선택되어 있으면 공장명 옵션 로드
    if (initData.팀업체명 !== "전체") {
      await this.loadOptionsForLevel("공장명", [initData.팀업체명]);

      // 3. 공장명이 선택되어 있으면 대분류 옵션 로드
      if (initData.공장명 !== "전체") {
        await this.loadOptionsForLevel("대분류", [initData.팀업체명, initData.공장명]);

        // 4. 대분류가 선택되어 있으면 중분류 옵션 로드
        if (initData.대분류 !== "전체") {
          await this.loadOptionsForLevel("중분류", [initData.팀업체명, initData.공장명, initData.대분류]);

          // 5. 중분류가 선택되어 있으면 소분류 옵션 로드
          if (initData.중분류 !== "전체") {
            await this.loadOptionsForLevel("소분류", [initData.팀업체명, initData.공장명, initData.대분류, initData.중분류]);
          }
        }
      }
    }

    this.isInitializing = false;
  },
  mounted() {
    if (this.setRef) {
      // filter 모드에서 초기화 버튼 클릭시 검색조건을 초기화 하기 위해서 ref 를 연결해주기 위함
      this.setRef('searchFilterItem', this.$refs.searchFilterItem)
    }
  },
  methods: {
    emitValue() {
      this.$emit("input", {
        팀업체명: this.팀업체명,
        공장명: this.공장명,
        대분류: this.대분류,
        중분류: this.중분류,
        소분류: this.소분류,
      });
    },

    /**
     * 특정 레벨의 옵션을 계산한다.
     * 공정분류표(736/895) 전체를 한 번만 받아 두고(ClassificationTable 유틸이 캐시),
     * 상위 레벨 선택에 따른 단계별 필터링은 모두 프론트에서 계산한다.
     * @param {string} level - 로드할 레벨 ("팀업체명", "공장명", "대분류", "중분류", "소분류")
     * @param {Array} parentValues - 상위 레벨의 선택된 값들 (예: ["제강팀", "100톤제강"])
     */
    async loadOptionsForLevel(level, parentValues = []) {
      if (!this.boardIds || this.boardIds.length === 0) return ["전체"];

      const rows = await loadClassificationTable(this.$store, this.classificationBoardId);
      const options = buildLevelOptions(rows, level, { fieldName: this.fieldName, parentValues });

      this.cfOptions[this.optionKeyOf(level)] = options;
      return options;
    },

    // 검색 레벨명 -> cfOptions 키 (템플릿은 팀업체명 옵션을 cfOptions.팀명 으로 읽는다)
    optionKeyOf(level) {
      return level === "팀업체명" ? "팀명" : level;
    },
  },
};
</script>
